import React from "react";
import PropTypes from "prop-types";
import EntityProxy from "data/util/EntityProxy";
import { descriptorPropTypes } from "lib/common/descriptorcontrols/formhelpers";
import RecordActions, { OPT_USER_INITIATED } from "data/RecordActions";
import Dispatcher from "Dispatcher";
import shallowEqual from "lib/shallowEqual";

export function deriveValueAndEnabledFromProps(props) {
    const { activeRecord, descriptor } = props;
    return {
        value: activeRecord.get(descriptor.key),
        enabled: activeRecord.isFieldEnabled(descriptor.key),
    };
}

export class BaseFormItem extends React.Component {
    constructor(props) {
        super(props);

        this.onCommitChange = this.onCommitChange.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.state = {
            pureOverValueAndEnabled: false,
        };
    }

    /**
     * Call _from the constructor_ to indicate this component is pure over its
     * value and enable state.
     * @protected
     */
    __enablePureOverValueAndEnabledHandling() {
        // eslint-disable-next-line react/no-direct-mutation-state
        this.state = {
            pureOverValueAndEnabled: true,
            ...deriveValueAndEnabledFromProps(this.props)
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.state.pureOverValueAndEnabled) {
            this.setState(deriveValueAndEnabledFromProps(nextProps));
        }
    }

    shouldComponentUpdate(
        nextProps,
        nextState,
        nextContext,
    ) {
        return this.state.pureOverValueAndEnabled
            ? !shallowEqual(this.state, nextState)
            : true;
    }

    // Most of the controls handle events the same way. Only a few need to
    // overload the following functions.
    onCommitChange(d, v, userInvoked = true) {
        // set the active record value
        const {descriptor, activeRecord} = this.props;
        if (d.key !== descriptor.key)
            throw new Error(`Inconsistent descriptor for ${activeRecord.getKey()}: ${d.key} passed, ${descriptor.key} from props.`);
        this.doSetField(activeRecord, descriptor.key, v, userInvoked);
    }

    doSetField(record, fieldKey, value, userInvoked = true) {
        const {fluxContext} = this.props;
        Dispatcher.contextDispatch(fluxContext, {
            type: RecordActions.SET_FIELD,
            recordKey: record.entityKey,
            recordId: record.id,
            fieldKey,
            value,
            [OPT_USER_INITIATED]: userInvoked,
        });
    }

    onFocus(e, d, v, k) { // (event, descriptor, value, keyboardShortcuts)
        const {onFocus} = this.props;
        // moveFocus expects the target to be the .react-control which has the field id
        e.currentTarget = $(e.currentTarget).parents(".react-control").get(0);
        onFocus && onFocus(e, k);
    }

    onBlur(e, d, v) { // (event, descriptor, value)
        const {onBlur} = this.props;
        // moveFocus expects the target to be the .react-control which has the field id
        e.currentTarget = $(e.currentTarget).parents(".react-control").get(0);
        onBlur && onBlur(e);
    }

    onKeyDown(e, d, v, isKeyboardShortcut) { // (event, descriptor, value, isKeyboardShortcut)
        const {onKeyDown} = this.props;
        onKeyDown && onKeyDown(e, isKeyboardShortcut);
    }

    getPlumbingProps() {
        return {
            controlOnly: this.props.controlOnly,
            descriptor: this.props.descriptor,
            enabled: true,

            // why this block?
            valueKey: undefined,
            labelKey: undefined,
            options: undefined,
            placeholder: undefined,
            // end questionable block...

            // callbacks
            onCommitChange: this.onCommitChange,

            // EntityForm is handling this callback at the moment with .control
            onKeyDown: this.onKeyDown, // (event, descriptor, value) callback function

            // EntityForm is not tracking .react-control focus or blur events.
            // onFocus and onBlur should not need to be overloaded.
            onFocus: this.onFocus,
            onBlur: this.onBlur,
        };
    }

}

// initial PropTypes for all controls that extend BaseFormItem
BaseFormItem.propTypes = {
    descriptor: PropTypes.shape(descriptorPropTypes()),
    fluxContext: PropTypes.string.isRequired,
    activeRecord: PropTypes.instanceOf(EntityProxy).isRequired,
    controlOnly: PropTypes.bool,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func.isRequired,
};

export default BaseFormItem;



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/dataentryform/formitems/BaseFormItem.js